import { useState } from "react"
import { useSelector } from "react-redux"
import { MySelector } from "../../redux/store"
import { toast } from "sonner"

const API_BASE = process.env.REACT_APP_API_BASE_URL

interface Props {
  open: boolean
  onClose: () => void
}

const DownloadAllModal = ({ open, onClose }: Props) => {
  const { data: eventData, eventId } = MySelector((state) => state.event)
  const { theme } = MySelector((state) => state.theme)
  const [email, setEmail] = useState("")
  const [loading, setLoading] = useState(false)
  const [submitted, setSubmitted] = useState(false)

  if (!open) return null

  const handleSubmit = async () => {
    if (!email || !email.includes("@")) {
      toast.error("Please enter a valid email address")
      return
    }
    setLoading(true)
    try {
      const res = await fetch(
        `${API_BASE}/download/request?eventId=${eventId}&email=${encodeURIComponent(email)}`,
        { method: "POST" }
      )
      const data = await res.json()
      if (data?.status === "OK") {
        setSubmitted(true)
      } else {
        toast.error("Failed to initiate download. Please try again.")
      }
    } catch {
      toast.error("Failed to initiate download. Please try again.")
    } finally {
      setLoading(false)
    }
  }

  const isDark = theme === "dark"
  const bg = isDark ? "#0d0d0d" : "#ffffff"
  const text = isDark ? "#e1e1e1" : "#1a1a1a"
  const subtext = isDark ? "#706f6c" : "#888888"
  const border = isDark ? "#26272c" : "#e0e0e0"
  const inputBg = isDark ? "#1a1b1f" : "#f5f5f5"

  return (
    <div
      style={{
        position: "fixed", inset: 0, zIndex: 10000,
        display: "flex", alignItems: "center", justifyContent: "center",
        background: "rgba(0,0,0,0.6)", padding: "16px",
      }}
      onClick={onClose}
    >
      <div
        style={{
          background: bg, borderRadius: "16px", border: `1px solid ${border}`,
          padding: "32px", maxWidth: "420px", width: "100%",
        }}
        onClick={(e) => e.stopPropagation()}
      >
        {submitted ? (
          <>
            <p style={{ color: text, fontWeight: 600, fontSize: "18px", marginBottom: "12px" }}>
              Preparing your download
            </p>
            <p style={{ color: subtext, fontSize: "14px", lineHeight: 1.6 }}>
              Your gallery is being packaged into ZIP files. You'll receive an email at{" "}
              <strong style={{ color: text }}>{email}</strong> with download links when it's ready.
              Links expire in 7 days.
            </p>
            <button
              onClick={onClose}
              style={{
                marginTop: "24px", width: "100%", padding: "12px",
                background: "linear-gradient(90deg, #F6C67C 0%, #A58447 100%)",
                color: "#08090B", border: "none", borderRadius: "40px",
                fontWeight: 600, cursor: "pointer", fontSize: "14px",
              }}
            >
              Done
            </button>
          </>
        ) : (
          <>
            <p style={{ color: text, fontWeight: 600, fontSize: "18px", marginBottom: "8px" }}>
              Download All Photos
            </p>
            <p style={{ color: subtext, fontSize: "13px", marginBottom: "24px", lineHeight: 1.6 }}>
              Photos will be packaged into ZIP files of 100 photos each. Videos are sent as
              individual links. Enter your email to receive the download links.
            </p>
            <label style={{ color: subtext, fontSize: "12px", display: "block", marginBottom: "6px" }}>
              Email address
            </label>
            <input
              type="email"
              placeholder="you@example.com"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              onKeyDown={(e) => { if (e.key === "Enter") handleSubmit() }}
              style={{
                width: "100%", padding: "10px 14px", borderRadius: "8px",
                border: `1px solid ${border}`, background: inputBg, color: text,
                fontSize: "14px", outline: "none", boxSizing: "border-box",
              }}
            />
            <div style={{ display: "flex", gap: "12px", marginTop: "24px" }}>
              <button
                onClick={onClose}
                style={{
                  flex: 1, padding: "12px", background: "transparent",
                  color: isDark ? "#F6C67C" : "#666", border: `1px solid ${border}`,
                  borderRadius: "40px", cursor: "pointer", fontSize: "14px",
                }}
              >
                Cancel
              </button>
              <button
                onClick={handleSubmit}
                disabled={loading || !email}
                style={{
                  flex: 1, padding: "12px",
                  background: email
                    ? "linear-gradient(90deg, #F6C67C 0%, #A58447 100%)"
                    : "#26272c",
                  color: email ? "#08090B" : "#706f6c",
                  border: "none", borderRadius: "40px",
                  fontWeight: 600, cursor: email ? "pointer" : "not-allowed",
                  fontSize: "14px",
                }}
              >
                {loading ? "Submitting..." : "Send me the links"}
              </button>
            </div>
          </>
        )}
      </div>
    </div>
  )
}

export default DownloadAllModal
